import { useRef, useState, type FormEvent } from "react";

interface JobApplicationFormProps {
  role: string;
  submitLabel?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  mobile: string;
  currentLocation: string;
  currentJobTitle: string;
  totalExperience: string;
  linkedin: string;
  portfolio: string;
  coverLetter: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  resume?: string;
}

type SubmitStatus = "idle" | "submitting" | "success";

const COUNTRY_CODES = [
  { code: "+1", label: "US / CA (+1)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+91", label: "India (+91)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+65", label: "Singapore (+65)" },
];

const ACCEPTED_TYPES = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"];
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

const inputBase =
  "w-full px-4 py-3 rounded-xl bg-background-100 border text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all";
const inputNormal =
  "border-background-200/70 focus:ring-accent-500/40 focus:border-accent-400/50";
const inputError =
  "border-rose-400/70 focus:ring-rose-400/40 focus:border-rose-400/50";

function Field({
  label,
  required,
  error,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-foreground-800">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && !error && <span className="text-xs text-foreground-400">{hint}</span>}
      {error && <span className="text-xs font-medium text-rose-500">{error}</span>}
    </div>
  );
}

export default function JobApplicationForm({
  role,
  submitLabel = "Submit Application",
}: JobApplicationFormProps) {
  const [values, setValues] = useState<FormValues>({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+1",
    mobile: "",
    currentLocation: "",
    currentJobTitle: "",
    totalExperience: "",
    linkedin: "",
    portfolio: "",
    coverLetter: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [resume, setResume] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setField = (key: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateResume = (file: File): string => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_TYPES.includes(file.type) && !ACCEPTED_EXTENSIONS.includes(ext)) {
      return "Please upload a PDF, DOC or DOCX file.";
    }
    if (file.size > MAX_SIZE) {
      return "File size must be 5 MB or smaller.";
    }
    return "";
  };

  const handleFileChange = (file: File | undefined) => {
    setResumeError("");
    if (!file) return;
    const err = validateResume(file);
    if (err) {
      setResumeError(err);
      setResume(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setResume(file);
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!values.firstName.trim()) next.firstName = "First name is required.";
    if (!values.lastName.trim()) next.lastName = "Last name is required.";
    if (!values.email.trim()) {
      next.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = "Please enter a valid email address.";
    }
    if (!values.mobile.trim() || values.mobile.replace(/\D/g, "").length < 7) {
      next.mobile = "Please enter a valid mobile number.";
    }
    if (!resume) next.resume = "Resume / CV is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    // Frontend-ready submission — no backend is connected, so nothing is stored on a server.
    setTimeout(() => {
      setStatus("success");
    }, 1400);
  };

  const resetForm = () => {
    setValues({
      firstName: "",
      lastName: "",
      email: "",
      countryCode: "+1",
      mobile: "",
      currentLocation: "",
      currentJobTitle: "",
      totalExperience: "",
      linkedin: "",
      portfolio: "",
      coverLetter: "",
    });
    setResume(null);
    setResumeError("");
    setErrors({});
    setStatus("idle");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-8 md:p-10 text-center">
        <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-500">
          <i className="ri-check-line text-3xl" />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading">
          Application Submitted
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
          Thank you for applying{role !== "General Application" ? ` for the ${role} role` : ""}. Our
          team will review your details and get in touch if your profile matches.
        </p>
        <button
          onClick={resetForm}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-background-200/70 text-foreground-700 text-sm font-semibold rounded-xl hover:bg-background-100 transition-all"
        >
          Submit Another Application
          <i className="ri-arrow-right-line text-xs" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="First Name" required error={errors.firstName}>
          <input
            type="text"
            name="firstName"
            value={values.firstName}
            onChange={(e) => setField("firstName", e.target.value)}
            placeholder="John"
            className={`${inputBase} ${errors.firstName ? inputError : inputNormal}`}
            aria-invalid={!!errors.firstName}
          />
        </Field>

        <Field label="Last Name" required error={errors.lastName}>
          <input
            type="text"
            name="lastName"
            value={values.lastName}
            onChange={(e) => setField("lastName", e.target.value)}
            placeholder="Doe"
            className={`${inputBase} ${errors.lastName ? inputError : inputNormal}`}
            aria-invalid={!!errors.lastName}
          />
        </Field>

        <Field label="Email" required error={errors.email}>
          <input
            type="email"
            name="email"
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="you@example.com"
            className={`${inputBase} ${errors.email ? inputError : inputNormal}`}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field label="Mobile Number" required error={errors.mobile}>
          <div className="flex gap-2">
            <select
              name="countryCode"
              value={values.countryCode}
              onChange={(e) => setField("countryCode", e.target.value)}
              aria-label="Country code"
              className={`${inputBase} !w-[110px] shrink-0 ${inputNormal}`}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              name="mobile"
              value={values.mobile}
              onChange={(e) => setField("mobile", e.target.value)}
              placeholder="555 123 4567"
              className={`${inputBase} ${errors.mobile ? inputError : inputNormal}`}
              aria-invalid={!!errors.mobile}
            />
          </div>
        </Field>

        <Field label="Current Location">
          <input
            type="text"
            name="currentLocation"
            value={values.currentLocation}
            onChange={(e) => setField("currentLocation", e.target.value)}
            placeholder="City, Country"
            className={`${inputBase} ${inputNormal}`}
          />
        </Field>

        <Field label="Current Job Title">
          <input
            type="text"
            name="currentJobTitle"
            value={values.currentJobTitle}
            onChange={(e) => setField("currentJobTitle", e.target.value)}
            placeholder="e.g. Senior Laravel Developer"
            className={`${inputBase} ${inputNormal}`}
          />
        </Field>

        <Field label="Total Experience">
          <select
            name="totalExperience"
            value={values.totalExperience}
            onChange={(e) => setField("totalExperience", e.target.value)}
            className={`${inputBase} ${inputNormal}`}
          >
            <option value="">Select experience</option>
            <option value="0-1">Less than 1 year</option>
            <option value="1-2">1 - 2 years</option>
            <option value="2-4">2 - 4 years</option>
            <option value="4-6">4 - 6 years</option>
            <option value="6-8">6 - 8 years</option>
            <option value="8+">8+ years</option>
          </select>
        </Field>

        <Field label="LinkedIn Profile">
          <input
            type="url"
            name="linkedin"
            value={values.linkedin}
            onChange={(e) => setField("linkedin", e.target.value)}
            placeholder="https://linkedin.com/in/username"
            className={`${inputBase} ${inputNormal}`}
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Portfolio / Website">
            <input
              type="url"
              name="portfolio"
              value={values.portfolio}
              onChange={(e) => setField("portfolio", e.target.value)}
              placeholder="https://yourwebsite.com"
              className={`${inputBase} ${inputNormal}`}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Cover Letter / Message">
            <textarea
              name="coverLetter"
              value={values.coverLetter}
              onChange={(e) => setField("coverLetter", e.target.value)}
              rows={5}
              maxLength={500}
              placeholder="Tell us a bit about yourself and why you'd be a great fit..."
              className={`${inputBase} resize-none ${inputNormal}`}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Resume / CV" required error={resumeError || errors.resume} hint="PDF, DOC or DOCX. Maximum 5 MB.">
            <input
              ref={fileInputRef}
              type="file"
              name="resume"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => handleFileChange(e.target.files?.[0])}
              className="hidden"
              aria-label="Upload resume"
            />
            {resume ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between rounded-xl border border-accent-300/40 bg-accent-50/40 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                    <i className="ri-file-text-line text-lg" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950 truncate">{resume.name}</p>
                    <p className="text-xs text-foreground-500">
                      {(resume.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setResume(null);
                    setResumeError("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-500 hover:text-rose-600 transition-colors shrink-0"
                >
                  <i className="ri-delete-bin-line" />
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-background-300 px-4 py-6 text-sm font-semibold text-foreground-600 hover:border-accent-400 hover:text-accent-600 transition-all"
              >
                <i className="ri-upload-2-line text-lg" />
                Click to upload your resume
              </button>
            )}
          </Field>
        </div>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {status === "submitting" ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              {submitLabel}
              <i className="ri-send-plane-line text-xs" />
            </>
          )}
        </button>
        <p className="text-xs text-foreground-400 leading-relaxed">
          Fields marked <span className="text-rose-500">*</span> are required.
        </p>
      </div>
    </form>
  );
}